import React, { useEffect, useState } from 'react';
import { Button, Col, Empty, InputNumber, Modal, Row, Space, Typography } from 'antd';
import SoftTag from '../components/SoftTag';
import { brandSemantic } from '../config/brand';
import { useI18n } from '../i18n';
import { isExpiringSoon, isValid } from '../utils/certificateHelpers';

const { Text, Title } = Typography;

const ROLE_PRICE_FIELD = {
  Product: 'avgItemPrice',
  Production: 'avgProductionPrice',
  Branding: 'avgBrandingPrice',
  Delivery: 'avgDeliveryPricePerItemPerKm',
};

const ROLE_LABELS = {
  Product: 'Product Supplier',
  Production: 'Production Supplier',
  Branding: 'Branding Supplier',
  Delivery: 'Delivery Supplier',
};

/**
 * SupplierComparisonDialog
 *
 * Bordered-tile supplier comparison view for assigning a single Quote Product
 * role (Product/Production/Branding/Delivery — see `role`).
 *
 * When `product` is provided (catalogue-linked Quote Product): one tile per
 * Supplier Product linked to it that has a non-null price for this role's
 * price field (`avgItemPrice`/`avgProductionPrice`/`avgBrandingPrice`/
 * `avgDeliveryPricePerItemPerKm`).
 *
 * When `product` is null (new/unmatched product — no catalogue link yet):
 * falls back to one tile per active Supplier with a manually-entered price.
 *
 * Tiles for suppliers already assigned to any role on another item in the
 * same Quote (`usedSupplierIds`) show an "Already used in this Quote" tag —
 * informational only, the tile remains selectable.
 *
 * On "Use This Supplier": calls `onSelect(role, { supplierId, supplierName, price })`.
 */
export default function SupplierComparisonDialog({
  open,
  onCancel,
  onSelect,
  role = 'Product',
  product = null,
  suppliers = [],
  supplierProducts = [],
  certificates = [],
  usedSupplierIds = new Set(),
}) {
  const { t } = useI18n();
  const [manualPrices, setManualPrices] = useState({});

  useEffect(() => {
    if (!open) return;
    const initial = {};
    suppliers.forEach((s) => { initial[s.id] = 0; });
    setManualPrices(initial);
  }, [open, suppliers]);

  const priceField = ROLE_PRICE_FIELD[role];
  const roleLabel = t(ROLE_LABELS[role] || role);

  const catalogueTiles = product
    ? supplierProducts
      .filter((sp) => sp.productId === product.id && sp[priceField] != null)
      .map((sp) => {
        const supplier = suppliers.find((item) => item.id === sp.supplierId);
        return { supplier, supplierProduct: sp, price: sp[priceField] };
      })
      .filter((tile) => tile.supplier)
    : [];

  const fallbackTiles = !product
    ? suppliers
      .filter((s) => s.status !== 'Inactive')
      .map((supplier) => ({ supplier, supplierProduct: null, price: manualPrices[supplier.id] ?? 0 }))
    : [];

  const tiles = product ? catalogueTiles : fallbackTiles;

  const handleUse = (tile) => {
    onSelect?.(role, {
      supplierId: tile.supplier.id,
      supplierName: tile.supplier.name,
      price: tile.price,
    });
  };

  return (
    <Modal
      title={`${t('Select Supplier')} — ${roleLabel}`}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={760}
    >
      {tiles.length === 0 ? (
        <Empty description={t('No eligible suppliers for this role')} />
      ) : (
        <Row gutter={[16, 16]}>
          {tiles.map((tile) => {
            const alreadyUsed = usedSupplierIds.has(tile.supplier.id);
            const supplierCertificates = certificates.filter((cert) => cert.supplierId === tile.supplier.id);
            const validCertCount = supplierCertificates.filter((cert) => isValid(cert.expiryDate)).length;
            const hasExpiringSoon = supplierCertificates.some((cert) => isExpiringSoon(cert.expiryDate));
            const hasNoCerts = supplierCertificates.length === 0;
            const hasProductCert = Boolean(product) && supplierCertificates.some((cert) => cert.productId === product.id);
            return (
              <Col xs={24} sm={12} key={tile.supplier.id}>
                <div
                  style={{
                    border: `1px solid ${alreadyUsed ? brandSemantic.primary : brandSemantic.border}`,
                    borderRadius: 10,
                    padding: 14,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <Space align="center" wrap size={6}>
                    <Title level={5} style={{ margin: 0 }}>{tile.supplier.name}</Title>
                    {tile.supplier.supplierType && (
                      <SoftTag tone={{ color: brandSemantic.textSecondary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted }} style={{ fontSize: 11 }}>
                        {t(tile.supplier.supplierType)}
                      </SoftTag>
                    )}
                    {alreadyUsed && (
                      <SoftTag tone={{ color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder }} style={{ fontSize: 11 }}>
                        {t('Already used in this Quote')}
                      </SoftTag>
                    )}
                  </Space>

                  <Space wrap size={6}>
                    {hasNoCerts ? (
                      <SoftTag tone={{ color: brandSemantic.textSecondary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted }} style={{ fontSize: 11 }}>
                        {t('No certificates')}
                      </SoftTag>
                    ) : (
                      <SoftTag tone={{ color: brandSemantic.textSecondary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted }} style={{ fontSize: 11 }}>
                        {validCertCount} {validCertCount === 1 ? t('cert') : t('certs')}
                      </SoftTag>
                    )}
                    {hasExpiringSoon && (
                      <SoftTag tone={{ color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder }} style={{ fontSize: 11 }}>
                        {t('Expiring Soon')}
                      </SoftTag>
                    )}
                    {hasProductCert && (
                      <SoftTag tone={{ color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder }} style={{ fontSize: 11 }}>
                        {t('Product Cert')}
                      </SoftTag>
                    )}
                  </Space>

                  {tile.supplierProduct ? (
                    <>
                      {tile.supplierProduct.samplePhotoUrl && (
                        <img
                          src={tile.supplierProduct.samplePhotoUrl}
                          alt={tile.supplierProduct.productName}
                          style={{ maxWidth: '100%', maxHeight: 90, borderRadius: 6, objectFit: 'cover' }}
                        />
                      )}
                      <Text style={{ fontSize: 13 }}>{tile.supplierProduct.productName}</Text>
                      {tile.supplierProduct.supplierItemNumber && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t('Supplier Item Number')}: {tile.supplierProduct.supplierItemNumber}
                        </Text>
                      )}
                      {tile.supplierProduct.description && (
                        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                          {tile.supplierProduct.description}
                        </Text>
                      )}
                      <Text strong>
                        {t(ROLE_LABELS[role])}: {Number(tile.price).toLocaleString('da-DK')} DKK
                      </Text>
                    </>
                  ) : (
                    <Space align="center" size={8}>
                      <Text type="secondary" style={{ fontSize: 13 }}>{roleLabel}:</Text>
                      <InputNumber
                        min={0}
                        step={0.01}
                        precision={2}
                        value={manualPrices[tile.supplier.id] ?? 0}
                        onChange={(val) => setManualPrices((prev) => ({ ...prev, [tile.supplier.id]: val ?? 0 }))}
                        style={{ width: 130 }}
                      />
                    </Space>
                  )}

                  <Button type="primary" size="small" style={{ alignSelf: 'flex-start', marginTop: 'auto' }} onClick={() => handleUse(tile)}>
                    {t('Use This Supplier')}
                  </Button>
                </div>
              </Col>
            );
          })}
        </Row>
      )}
    </Modal>
  );
}
